import { patientAuthApi } from '../api';
import { setAuthContext, clearAuthContext, setPatientToken, ApiError } from '../../../api/http';
import {
  generateNeoHealthCardId,
  ENTITY_TYPES,
} from '@shared/identity/neohealthcard-id-adapter';

const STORAGE_KEY = 'nhc_patient_onboarding';

/**
 * ApiError.message is always the generic "API Error 401: Unauthorized" —
 * the backend's actual reason (e.g. "Invalid code") lives in the response
 * body. Prefer that so users see "Invalid code" instead of "Unauthorized".
 */
function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | null | undefined;
    if (body?.message) return body.message;
  }
  return e instanceof Error ? e.message : 'Something went wrong. Please try again.';
}

export type ApprovalStatus = 'none' | 'pending' | 'approved' | 'rejected' | 'need_update';
export type EntryMode = 'login' | 'register' | 'none';
export type LoginMethod = 'phone' | 'email' | 'google' | 'none';

export interface OnboardingState {
  selectedRole: string | null;
  entryMode: EntryMode;
  loginMethod: LoginMethod;
  phone: string;
  email: string;
  fullName: string;
  dob: string;
  gender: string;
  bloodGroup: string;
  language: string;
  address: string;
  city: string;
  state: string;
  pin: string;
  otpVerified: boolean;
  registrationStarted: boolean;
  basicCompleted: boolean;
  contactCompleted: boolean;
  healthCompleted: boolean;
  emergencyCompleted: boolean;
  // 'self_reported' = patient-typed ABHA number, unverified (no ABDM
  // integration); 'linked' is reserved for a future real verification.
  abhaStatus: 'linked' | 'skipped' | 'none' | 'self_reported';
  abhaId: string;
  consentAccepted: boolean;
  consents: Record<string, boolean>;
  registrationSubmitted: boolean;
  nhcId: string;
  neoVerifyId: string;
  nhcIdGenerated: boolean;
  approvalStatus: ApprovalStatus;
  dashboardReady: boolean;
  allergies: string[];
  conditions: string[];
  medicines: string;
  surgeries: string[];
  familyHistory: string[];
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelation: string;
}

const DEFAULT_STATE: OnboardingState = {
  selectedRole: null,
  entryMode: 'none',
  loginMethod: 'none',
  phone: '',
  email: '',
  fullName: '',
  dob: '',
  gender: '',
  bloodGroup: '',
  language: '',
  address: '',
  city: '',
  state: '',
  pin: '',
  otpVerified: false,
  registrationStarted: false,
  basicCompleted: false,
  contactCompleted: false,
  healthCompleted: false,
  emergencyCompleted: false,
  abhaStatus: 'none',
  abhaId: '',
  consentAccepted: false,
  consents: { doctor: true, hospital: true, lab: true, pharmacy: true, emergency: true },
  registrationSubmitted: false,
  nhcId: '',
  neoVerifyId: '',
  nhcIdGenerated: false,
  approvalStatus: 'none',
  dashboardReady: false,
  allergies: [],
  conditions: [],
  medicines: '',
  surgeries: [],
  familyHistory: [],
  emergencyName: '',
  emergencyPhone: '',
  emergencyRelation: '',
};

function load(): OnboardingState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_STATE };
}

function save(state: OnboardingState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* quota */ }
}

let _state = load();
const _listeners = new Set<() => void>();

function emit() {
  save(_state);
  _listeners.forEach(fn => fn());
}

/**
 * The local-only login/approval fallback (no backend verification) is a demo
 * convenience for running the UI without a live backend.
 *
 * Hard-gated to DEV BUILDS: a production bundle can never enable it, even if
 * VITE_ENABLE_DEMO_AUTH leaks in as "true" — with it on, any phone + any
 * 6 digits "logs in", which must be impossible in anything deployed.
 */
export function isDemoAuthEnabled(): boolean {
  return import.meta.env.DEV === true && import.meta.env?.VITE_ENABLE_DEMO_AUTH === 'true';
}

/** @deprecated Legacy ID generator — produces NHC-PAT-{ts}{rand} format. Kept for backward compatibility. */
export function generateLegacyNHCId(): string {
  const ts = Date.now().toString(36).slice(-4).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `NHC-PAT-${ts}${rand}`;
}

/** Generate a canonical 12-digit NeoHealthCard ID for a patient. */
export function generateNHCId(): string {
  return generateNeoHealthCardId(ENTITY_TYPES.PATIENT);
}

export const patientAuthFlow = {
  getState: () => _state,

  subscribe(fn: () => void) {
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  },

  getRequiredRoute(): string {
    if (_state.dashboardReady && _state.approvalStatus === 'approved') return '/patient';
    if (_state.approvalStatus === 'pending') return '/patient/approval-pending';
    if (_state.approvalStatus === 'rejected' || _state.approvalStatus === 'need_update') return '/patient/verification-update-required';
    return '/patient/entry';
  },

  isOnboardingComplete(): boolean {
    return _state.dashboardReady && _state.approvalStatus === 'approved';
  },

  // --- Entry Choice ---
  setEntryMode(mode: EntryMode) {
    _state = { ..._state, entryMode: mode };
    emit();
  },

  setLoginMethod(method: LoginMethod) {
    _state = { ..._state, loginMethod: method };
    emit();
  },

  // --- Login flow ---
  loginVerifyOTP() {
    _state = { ..._state, otpVerified: true };
    emit();
  },

  checkApprovalAfterLogin(): ApprovalStatus {
    if (_state.approvalStatus === 'approved') {
      _state = { ..._state, dashboardReady: true };
      emit();
    }
    return _state.approvalStatus;
  },

  // --- Registration flow ---
  startRegistration() {
    _state = { ..._state, registrationStarted: true };
    emit();
  },

  completeBasic(data: { fullName: string; dob: string; gender: string; bloodGroup: string }) {
    _state = { ..._state, ...data, basicCompleted: true };
    emit();
  },

  completeContact(data: { phone: string; email: string }) {
    _state = { ..._state, ...data, contactCompleted: true };
    emit();
  },

  completeAddress(data: { address: string; city: string; state: string; pin: string }) {
    _state = { ..._state, ...data };
    emit();
  },

  verifyOTP() {
    _state = { ..._state, otpVerified: true };
    emit();
  },

  completeHealth(data: Partial<Pick<OnboardingState, 'allergies' | 'conditions' | 'medicines' | 'surgeries' | 'familyHistory'>>) {
    _state = { ..._state, ...data, healthCompleted: true };
    emit();
  },

  completeEmergency(data: { emergencyName: string; emergencyPhone: string; emergencyRelation: string }) {
    _state = { ..._state, ...data, emergencyCompleted: true };
    emit();
  },

  // 'self_reported' = the patient typed an ABHA number we could not verify
  // (no ABDM integration exists) — it must never be presented as "linked".
  completeAbha(status: 'linked' | 'skipped' | 'self_reported', abhaId?: string) {
    _state = { ..._state, abhaStatus: status, abhaId: abhaId || '' };
    emit();
  },

  acceptConsent(consents?: Record<string, boolean>) {
    _state = { ..._state, consentAccepted: true, ...(consents ? { consents } : {}) };
    emit();
  },

  submitRegistration() {
    const nhcId = generateNHCId();
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    // Canonical NH12 is pure digits — take last 6 for NeoVerify shorthand
    const last6 = nhcId.replace(/\D/g, '').slice(-6);
    _state = {
      ..._state,
      nhcId,
      neoVerifyId: `NV-${last6}-${mm}${dd}`,
      nhcIdGenerated: true,
      registrationSubmitted: true,
      approvalStatus: 'pending',
    };
    emit();
  },

  // --- Dev-only approval simulation ---
  devSetApprovalStatus(status: ApprovalStatus) {
    _state = { ..._state, approvalStatus: status };
    if (status === 'approved') {
      _state.dashboardReady = false;
    }
    emit();
  },

  // --- Reset ---
  reset() {
    _state = { ...DEFAULT_STATE };
    emit();
  },

  resetToLogin() {
    const preserved = {
      phone: _state.phone,
      email: _state.email,
      fullName: _state.fullName,
      nhcId: _state.nhcId,
      neoVerifyId: _state.neoVerifyId,
      nhcIdGenerated: _state.nhcIdGenerated,
      approvalStatus: _state.approvalStatus,
      dob: _state.dob,
      gender: _state.gender,
      bloodGroup: _state.bloodGroup,
    };
    _state = { ...DEFAULT_STATE, ...preserved, entryMode: 'login' as EntryMode };
    emit();
  },

  // --- Backend adapter methods ---
  // These call the real backend and update local state.
  // If backend is unavailable, the existing localStorage flow continues to work for preview.

  async backendRegister(): Promise<{ success: boolean; error?: string }> {
    try {
      const s = _state;
      const result = await patientAuthApi.register({
        fullName: s.fullName,
        phone: s.phone || undefined,
        email: s.email || undefined,
        dob: s.dob || undefined,
        gender: s.gender || undefined,
        bloodGroup: s.bloodGroup || undefined,
        address: s.address || undefined,
        city: s.city || undefined,
        state: s.state || undefined,
        pin: s.pin || undefined,
        emergencyName: s.emergencyName || undefined,
        emergencyPhone: s.emergencyPhone || undefined,
        emergencyRelation: s.emergencyRelation || undefined,
        allergies: s.allergies.length ? s.allergies : undefined,
        conditions: s.conditions.length ? s.conditions : undefined,
        medicines: s.medicines || undefined,
        surgeries: s.surgeries.length ? s.surgeries : undefined,
        familyHistory: s.familyHistory.length ? s.familyHistory : undefined,
        abhaId: s.abhaId || undefined,
        abhaStatus: s.abhaStatus || undefined,
        consents: s.consents,
      });
      _state = {
        ..._state,
        nhcId: result.nhcId || _state.nhcId,
        neoVerifyId: result.neoVerifyId || _state.neoVerifyId,
        nhcIdGenerated: true,
        registrationSubmitted: true,
        approvalStatus: (result.approvalStatus as ApprovalStatus) || 'pending',
      };
      emit();
      return { success: true };
    } catch (e: any) {
      console.warn('[patientAuthFlow] Backend register failed, using local fallback:', e);
      return { success: false, error: errorMessage(e) };
    }
  },

  async backendRequestOtp(channel: 'phone' | 'email', identifier: string): Promise<{ success: boolean; otpRequestId?: string; demoOtp?: string; error?: string }> {
    try {
      const result = await patientAuthApi.requestOtp(channel, identifier);
      return { success: true, otpRequestId: result.otpRequestId, demoOtp: result.demoOtp };
    } catch (e: any) {
      console.warn('[patientAuthFlow] Backend OTP request failed:', e);
      return { success: false, error: errorMessage(e) };
    }
  },

  /**
   * Checked up front (step 1 of the registration wizard) so a duplicate
   * phone/email is caught immediately instead of only at final signup after
   * the user has filled out all 8 steps.
   */
  async backendCheckAvailability(data: { phone?: string; email?: string }): Promise<{ success: boolean; phoneExists?: boolean; emailExists?: boolean; error?: string }> {
    try {
      const result = await patientAuthApi.checkAvailability(data);
      return { success: true, phoneExists: result.phoneExists, emailExists: result.emailExists };
    } catch (e: any) {
      console.warn('[patientAuthFlow] Backend availability check failed:', e);
      return { success: false, error: errorMessage(e) };
    }
  },

  async backendVerifyOtp(data: { otpRequestId?: string; code: string; channel?: string; identifier?: string }): Promise<{ success: boolean; patient?: any; approvalStatus?: string; noAccount?: boolean; error?: string }> {
    try {
      const result = await patientAuthApi.verifyOtp(data);
      if (result.token) {
        setPatientToken(result.token);
      }
      if (result.patient) {
        setAuthContext({
          userId: result.patient.userId,
          institutionId: result.patient.institutionId || 'NHC',
          role: result.patient.role || 'PATIENT_USER',
          displayName: result.patient.displayName,
        });
        _state = {
          ..._state,
          otpVerified: true,
          nhcId: result.patient.nhcId || _state.nhcId,
          neoVerifyId: result.patient.neoVerifyId || _state.neoVerifyId,
          fullName: result.patient.displayName || _state.fullName,
          approvalStatus: (result.approvalStatus as ApprovalStatus) || _state.approvalStatus,
          dashboardReady: result.approvalStatus === 'approved',
        };
        emit();
      }
      // A correct code for a number with NO account verifies fine and comes
      // back empty — the same two endpoints serve login and pre-registration
      // contact checks. Saying so here is what keeps the login screen from
      // blaming the code (and from burning it: the code is spent now, so a
      // retry answers "expired", which is what patients were seeing).
      return {
        success: true,
        patient: result.patient,
        approvalStatus: result.approvalStatus,
        noAccount: !result.patient,
      };
    } catch (e: any) {
      console.warn('[patientAuthFlow] Backend OTP verify failed:', e);
      return { success: false, error: errorMessage(e) };
    }
  },

  async backendLogin(data: { phone?: string; email?: string; password?: string }): Promise<{ success: boolean; patient?: any; approvalStatus?: string; requiresVerification?: boolean; nextStep?: string; channel?: string; identifier?: string; requiresOtp?: boolean; otpRequestId?: string; maskedIdentifier?: string; demoOtp?: string; error?: string }> {
    try {
      const result = await patientAuthApi.login(data);
      if (result.requiresVerification) {
        return { success: true, requiresVerification: true, nextStep: result.nextStep, channel: result.channel, identifier: result.identifier };
      }
      // Two-factor step-up: password accepted, but the server issued no token
      // — an OTP sent to the account's own contact must be verified first.
      if (result.requiresOtp) {
        return {
          success: true, requiresOtp: true, otpRequestId: result.otpRequestId,
          maskedIdentifier: result.maskedIdentifier,
          demoOtp: isDemoAuthEnabled() ? result.devCode : undefined,
        };
      }
      if (result.token) {
        setPatientToken(result.token);
      }
      if (result.patient) {
        setAuthContext({
          userId: result.patient.userId,
          institutionId: result.patient.institutionId || 'NHC',
          role: result.patient.role || 'PATIENT_USER',
          displayName: result.patient.displayName,
        });
        _state = {
          ..._state,
          nhcId: result.patient.nhcId || _state.nhcId,
          neoVerifyId: result.patient.neoVerifyId || _state.neoVerifyId,
          fullName: result.patient.displayName || _state.fullName,
          phone: result.patient.phone || _state.phone,
          email: result.patient.email || _state.email,
          approvalStatus: (result.approvalStatus as ApprovalStatus) || _state.approvalStatus,
          dashboardReady: result.approvalStatus === 'approved',
        };
        emit();
      }
      return { success: true, patient: result.patient, approvalStatus: result.approvalStatus };
    } catch (e: any) {
      console.warn('[patientAuthFlow] Backend login failed:', e);
      return { success: false, error: errorMessage(e) };
    }
  },

  async backendCheckApproval(): Promise<{ success: boolean; approvalStatus?: string; error?: string }> {
    try {
      const result = await patientAuthApi.approvalStatus();
      if (result.approvalStatus) {
        _state = {
          ..._state,
          approvalStatus: result.approvalStatus as ApprovalStatus,
          dashboardReady: result.approvalStatus === 'approved',
          nhcId: result.nhcId || _state.nhcId,
        };
        emit();
      }
      return { success: true, approvalStatus: result.approvalStatus };
    } catch (e: any) {
      return { success: false, error: errorMessage(e) };
    }
  },

  async backendLogout(): Promise<void> {
    try {
      await patientAuthApi.logout();
    } catch { /* ignore */ }
    clearAuthContext();
    _state = { ...DEFAULT_STATE };
    emit();
  },
};
